import { useCallback } from "react";
import { FormProvider } from "react-hook-form";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { useInventoryTransferDialog } from "../hooks/useInventoryTransferDialog";
import { useInventoryTransferLines } from "../hooks/useInventoryTransferLines";
import { IInventoryTransferLineFormModel } from "../models/inventoryTransferModels";
import { useInventoryTransferProductSearch } from "../hooks/useInventoryTransferProductSearch";
import { InventoryTransferHeaderFields } from "./InventoryTransferHeaderFields";
import { InventoryTransferAddProductRow } from "./InventoryTransferAddProductRow";
import { InventoryTransferLinesTable } from "./InventoryTransferLinesTable";
import { InventoryTransferQuickAddProductDialog } from "./InventoryTransferQuickAddProductDialog";
import styles from "./InventoryTransferDialog.module.scss";

interface ITransferDialogProps {
  isOpen: boolean;
  editingTransferId: number | null;
  initialLines?: IInventoryTransferLineFormModel[] | null;
  onClose: () => void;
}

export const InventoryTransferDialog = ({
  isOpen,
  editingTransferId,
  initialLines,
  onClose,
}: ITransferDialogProps) => {
  const {
    form,
    mode,
    isFormDisabled,
    existingTransfer,
    handleSubmit,
    isSubmitting,
  } = useInventoryTransferDialog({
    editingTransferId,
    isOpen,
    onClose,
    initialLines: initialLines ?? null,
  });

  const linesHook = useInventoryTransferLines(form, isFormDisabled);

  const searchHook = useInventoryTransferProductSearch({
    disabled: isFormDisabled,
    onProductPicked: linesHook.addLineFromProduct,
  });

  const header =
    mode === "create"
      ? "Нова доставка"
      : `Редактиране на ${existingTransfer?.displayNumber ?? "доставка"}`;

  const hasLines = form.watch("lines")?.length > 0;

  const handleClose = useCallback(() => {
    if (!isFormDisabled && form.formState.isDirty) {
      showCustomConfirmDialog(
        "Имате несъхранени промени. Сигурни ли сте, че искате да затворите?",
        onClose,
        undefined,
        "warning",
        "Затвори",
        "Остани",
      );
      return;
    }
    onClose();
  }, [isFormDisabled, form.formState.isDirty, onClose]);

  return (
    <>
      <AppDialog visible={isOpen} header={header} onHide={handleClose} style={{ width: "min(920px, 96vw)" }}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <InventoryTransferHeaderFields disabled={isFormDisabled} />

            <RenderWhen condition={!isFormDisabled}>
              <InventoryTransferAddProductRow
                searchValue={searchHook.searchValue}
                onSearchChange={searchHook.setSearchValue}
                suggestions={searchHook.suggestionsForField}
                hasSearched={searchHook.hasSearched}
                onSuggestionSelect={searchHook.handleSuggestionSelect}
                onBarcodeScanned={searchHook.handleBarcodeScanned}
                onOpenQuickAdd={searchHook.openQuickAdd}
              />
            </RenderWhen>

            <InventoryTransferLinesTable
              lines={linesHook.lines}
              disabled={isFormDisabled}
              onQuantityChange={linesHook.updateQuantity}
              onExpirationChange={linesHook.updateExpiration}
              onRemove={linesHook.removeLine}
            />

            <RenderWhen condition={!hasLines}>
              <div className={styles.linesEmpty}>
                Добавете поне един продукт, за да запазите доставката.
              </div>
            </RenderWhen>

            <div className={styles.footer}>
              <DefaultButton
                label={isFormDisabled ? "Затвори" : "Отказ"}
                design="outline"
                onClick={handleClose}
                type="button"
                width="140px"
              />
              <RenderWhen condition={!isFormDisabled}>
                <DefaultButton
                  label="Запази"
                  type="submit"
                  loading={isSubmitting}
                  disabled={!hasLines}
                  width="140px"
                />
              </RenderWhen>
            </div>
          </form>
        </FormProvider>
      </AppDialog>

      <InventoryTransferQuickAddProductDialog
        visible={searchHook.isQuickAddOpen}
        onHide={searchHook.closeQuickAdd}
        onCreated={searchHook.handleQuickAddSuccess}
      />
    </>
  );
};
